import {
  type ClientConfig,
  createClient,
  type SanityClient,
} from '@sanity/client';

import { getClientValidatedEnv } from '../../config/client-env';
import type { SanityClientFetchOptions } from '../../data/sanity/types';
import { MetricsService } from '../MetricsService/MetricsService';

const getProjectSettings = (): Partial<ClientConfig> => {
  return {
    projectId:
      getClientValidatedEnv().NEXT_PUBLIC_SANITY_WORKSPACE ??
      getClientValidatedEnv().SANITY_STUDIO_WORKSPACE,
    dataset:
      getClientValidatedEnv().NEXT_PUBLIC_SANITY_DATASET ??
      getClientValidatedEnv().SANITY_STUDIO_DATASET,
    token: getClientValidatedEnv().SANITY_STUDIO_MANAGEMENT_TOKEN,
  };
};

const client = createClient({
  ...getProjectSettings(),
  useCdn: true,
  apiVersion: '2023-10-02',
});

export const getConfiguredClient = (
  options: SanityClientFetchOptions = {}
): SanityClient => {
  const config: any = {
    perspective: options.useRawPerspective ? 'raw' : 'published',
  };

  if (!options?.draftMode && options?.skipCdn) {
    config.useCdn = false;
    config.ignoreBrowserTokenWarning = true;
  }

  if (options?.draftMode) {
    const previewToken =
      getClientValidatedEnv().NEXT_PUBLIC_SANITY_PREVIEW_TOKEN;

    if (previewToken) {
      config.token = previewToken;
      config.useCdn = false;
      config.ignoreBrowserTokenWarning = true;
      config.perspective = 'previewDrafts';
    }
  }

  return client.withConfig(config);
};

export const executeQuery = async (
  query: string,
  options: SanityClientFetchOptions
): Promise<any> => {
  const configuredClient = getConfiguredClient(options);
  const result = await configuredClient.fetch(query, undefined, {
    filterResponse: false,
  });

  MetricsService.histogramObserve({
    name: 'sanity_fetch',
    value: result.ms / 1000,
  });

  return result.result;
};
